import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../i18n";
import { api } from "../api";
import { MASCOT } from "../constants/brand";
import { MdsBridge } from "./MdsBridge";
import { FlaskConical, HeartHandshake, ArrowUpRight, Stethoscope, Sparkles, ChevronDown, Quote } from "lucide-react";

export function RiseMoment() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/rise-moment?lang=${lang}`).then((r) => setData(r.data)).catch(() => {});
  }, [lang]);

  if (!data) return null;

  return (
    <div className="relative rounded-[2rem] mb-8 overflow-hidden bg-[#2B4A59] text-[#FDFBF7]" data-testid="rise-moment">
      <div className="grain opacity-40" />
      <img src={MASCOT.welcome} alt="Sole Rise" data-testid="sole-rise-mascot"
        className="absolute top-5 right-5 w-16 h-16 sm:w-24 sm:h-24 object-cover rounded-2xl shadow-lg animate-[pulse_4s_ease-in-out_infinite] pointer-events-none" />
      <button onClick={() => setOpen((o) => !o)} data-testid="maxim-toggle" className="relative w-full text-left p-8 pr-28 sm:pr-40 transition-colors hover:bg-white/5">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 rounded-full bg-[#C81D6B] animate-pulse" />
          <span className="text-[#B5651D] text-xs uppercase tracking-widest font-semibold">{t("maxim_of_day")}</span>
        </div>
        <div className="flex items-start gap-3">
          <Quote className="w-7 h-7 text-white/25 shrink-0 mt-1" />
          <p className="font-serif text-2xl sm:text-3xl leading-snug flex-1" data-testid="maxim-text">{data.maxim}</p>
        </div>
        <div className="flex items-center gap-1.5 mt-4 text-white/60 text-sm">
          <span>{open ? t("maxim_hide") : t("maxim_expand")}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="relative overflow-hidden"
          >
            <div className="px-8 pb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="rounded-full px-3 py-0.5 text-[11px] font-semibold bg-white/10 border border-white/15" data-testid="rise-theme">
                  {t("rise_theme")}: {data.theme?.label}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="rounded-2xl p-5 bg-white/5 border border-white/10" data-testid="rise-inspiration">
                  <div className="flex items-center gap-2 text-[#C81D6B] font-semibold mb-2">
                    <HeartHandshake className="w-4 h-4" /> {t("rise_inspiration")}
                  </div>
                  <p className="font-serif text-xl leading-snug mb-1.5">{data.inspiration?.title}</p>
                  <p className="text-[#cdd8dd] text-sm leading-relaxed mb-3">{data.inspiration?.text}</p>
                  <TaskChip label={t("rise_task")} text={data.inspiration?.task} color="#C81D6B" />
                </div>
                <div className="rounded-2xl p-5 bg-white/5 border border-white/10" data-testid="rise-science">
                  <div className="flex items-center gap-2 text-[#B5651D] font-semibold mb-2">
                    <FlaskConical className="w-4 h-4" /> {t("rise_science")}
                  </div>
                  <p className="font-serif text-xl leading-snug mb-1.5">{data.science?.title}</p>
                  <p className="text-[#cdd8dd] text-sm leading-relaxed mb-3">{data.science?.text}</p>
                  <TaskChip label={t("rise_task")} text={data.science?.task} color="#E08A3C" />
                </div>
              </div>

              <MdsBridge specialistUrl={data.specialist_url} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const TaskChip = ({ label, text, color }) => (
  <div className="flex items-start gap-2 rounded-xl bg-white/10 px-3 py-2">
    <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color }}>{label}</p>
      <p className="text-sm text-white/90 leading-snug">{text}</p>
    </div>
  </div>
);
